/**
 * <s-product-rotator> — 3D front/back turntable for product photos.
 * Drag horizontally (mouse, touch, pen) to spin [data-spinner]; release snaps to the nearest face.
 * [data-rotate="-1|1"] buttons turn half a revolution. Idles with a slow spin (not under reduced motion)
 * while its dialog is open. [data-look-option] buttons show the matching [data-look-stage].
 */
class SProductRotator extends HTMLElement {
  #ac;
  #raf = 0;
  #angle = 0;
  #dragging = false;
  #startX = 0;
  #startAngle = 0;
  #idle = true;
  #last = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.stage = this.querySelector('[data-stage]');
    this.dialog = this.closest('dialog');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.stage) return;

    this.stage.addEventListener('pointerdown', (e) => this.#down(e), opts);
    window.addEventListener('pointermove', (e) => this.#move(e), opts);
    window.addEventListener('pointerup', () => this.#up(), opts);
    window.addEventListener('pointercancel', () => this.#up(), opts);
    this.stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.#turn(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#turn(1); }
    }, opts);
    this.querySelectorAll('[data-rotate]').forEach((btn) => {
      btn.addEventListener('click', () => this.#turn(Number(btn.dataset.rotate)), opts);
    });
    this.querySelectorAll('[data-look-option]').forEach((btn) => {
      btn.addEventListener('click', () => this.#look(btn.dataset.lookOption), opts);
    });
    if (this.dialog) {
      this.dialog.addEventListener('close', () => this.#stop(), opts);
      new MutationObserver(() => { if (this.dialog.open) this.#start(); }).observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  get #spinner() {
    return this.querySelector('[data-look-stage]:not([hidden]) [data-spinner]');
  }

  #start() {
    this.#idle = true;
    this.#last = performance.now();
    cancelAnimationFrame(this.#raf);
    const loop = (t) => {
      const dt = t - this.#last;
      this.#last = t;
      if (this.#idle && !this.#dragging && !this.reduced.matches) {
        this.#angle += dt * 0.03;
        this.#apply(false);
      }
      this.#raf = requestAnimationFrame(loop);
    };
    this.#raf = requestAnimationFrame(loop);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
  }

  #apply(animate) {
    const spinner = this.#spinner;
    if (!spinner) return;
    spinner.style.transition = animate && !this.reduced.matches ? 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
    spinner.style.transform = `rotateY(${this.#angle}deg)`;
    const facingBack = Math.abs(((this.#angle % 360) + 360) % 360 - 180) < 90;
    this.querySelectorAll('[data-face-label]').forEach((el) => {
      el.hidden = el.dataset.faceLabel !== (facingBack ? 'back' : 'front');
    });
  }

  #down(e) {
    this.#dragging = true;
    this.#idle = false;
    this.#startX = e.clientX;
    this.#startAngle = this.#angle;
    this.stage.setPointerCapture?.(e.pointerId);
    this.stage.dataset.dragging = '';
  }

  #move(e) {
    if (!this.#dragging) return;
    this.#angle = this.#startAngle + (e.clientX - this.#startX) * 0.5;
    this.#apply(false);
  }

  #up() {
    if (!this.#dragging) return;
    this.#dragging = false;
    delete this.stage.dataset.dragging;
    this.#angle = Math.round(this.#angle / 180) * 180;
    this.#apply(true);
  }

  #turn(dir) {
    this.#idle = false;
    this.#angle = Math.round(this.#angle / 180) * 180 + dir * 180;
    this.#apply(true);
  }

  #look(value) {
    this.querySelectorAll('[data-look-stage]').forEach((el) => { el.hidden = el.dataset.lookStage !== value; });
    this.querySelectorAll('[data-look-option]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.lookOption === value)));
    this.#angle = 0;
    this.#apply(false);
  }
}

if (!customElements.get('s-product-rotator')) customElements.define('s-product-rotator', SProductRotator);
